import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { seriesList } from "@/mocks/series";

const featured = [...seriesList].sort((a, b) => b.hot - a.hot).slice(0, 5);

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  const goTo = useCallback((index: number) => {
    setActive((index + featured.length) % featured.length);
  }, []);

  useEffect(() => {
    timer.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % featured.length);
    }, 5200);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, []);

  const current = featured[active];

  return (
    <section
      className="relative h-[460px] w-full overflow-hidden bg-primary-950 md:h-[600px]"
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 50) {
          goTo(active + (delta < 0 ? 1 : -1));
        }
        touchStartX.current = null;
      }}
    >
      {featured.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === active ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 焦点剧照`}
            title={`${item.title} 仙侠剧集焦点图`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/55 to-primary-950/25"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-transparent"></div>

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1200px] flex-col justify-center px-4 md:px-6">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/90 px-3 py-1 text-xs font-semibold text-primary-950">
            <i className="ri-fire-fill"></i>
            本周焦点 · 仙侠经典
          </span>
          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {current.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-primary-100">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {current.year} 年首播
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-star-fill text-accent-400"></i>
              {current.rating.toFixed(1)} 分
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-film-line"></i>
              全 {current.episodes} 集
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-star-line"></i>
              {current.cast.slice(0, 2).join(" · ")}
            </span>
          </div>
          <p className="mt-5 line-clamp-2 max-w-xl text-sm leading-relaxed text-primary-100 md:text-base">
            {current.summary[0]}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看剧集详情
            </Link>
            <Link
              to={`/play/${current.id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/50 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-movie-2-line text-lg"></i>
              进入播放演示
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(active - 1)}
        className="absolute left-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:left-6 md:h-12 md:w-12"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(active + 1)}
        className="absolute right-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:right-6 md:h-12 md:w-12"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {featured.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => goTo(index)}
            className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
              index === active ? "w-8 bg-accent-500" : "w-3 bg-background-50/50"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}